// src/SignUp.js
import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  CssBaseline,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  useMediaQuery
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import darkTheme from './theme';

const referralOptions = [
  { value: 'Coinsniper', label: 'Coinsniper' },
  { value: 'Top100Token', label: 'Top100Token' },
  { value: 'GemsRadar', label: 'GemsRadar' },
  { value: 'Instagram', label: 'Instagram' },
  { value: 'X', label: 'X (Twitter)' },
  { value: 'Telegram', label: 'Telegram' },
];

function SignUp() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    referral: '',
    referralCode: '',
  });
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const isMobile = useMediaQuery('(max-width:600px)');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Updated handleSubmit function to call the new REST API endpoint.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.referral) {
      setError('Please fill in all required fields');
      return;
    }
    setError('');
    try {
      // Update the URL below to match your deployed site URL if needed.
      const response = await fetch('https://brokepiggytoken.xyz/wp-json/getearlyaccess/v1/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          referral: formData.referral,
          referralCode: formData.referralCode,
        })
      });
      const data = await response.json();
      if (data.success) {
        // Set the success message and clear form fields.
        setSuccessMessage(data.message || 'Request successful! Please check your email for a response.');
        setFormData({ name: '', email: '', referral: '', referralCode: '' });
      } else {
        setError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setError('An error occurred. Please try again later.');
    }
  };

  return (
    <>
      <CssBaseline />
      <Container
        maxWidth="sm"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            p: 4,
            borderRadius: '24px',
            background: 'linear-gradient(145deg, #181818 0%, #121212 100%)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.5)',
            border: '1px solid rgba(255, 215, 0, 0.1)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                mb: 1,
                background: 'linear-gradient(45deg, #FFD700 30%, #FFC400 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.5px',
              }}
            >
              Join CrowdLaunch Beta
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                fontSize: '1rem',
                lineHeight: 1.5,
                maxWidth: '600px',
                mx: 'auto',
              }}
            >
              Secure your spot as a blockchain pioneer. Get exclusive early access, premium rewards, and shape the future of decentralized innovation.
            </Typography>
          </Box>

          {error && (
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                p: 1.5,
                bgcolor: 'error.dark',
                color: 'error.contrastText',
                borderRadius: 1,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ⚠️ {error}
            </Typography>
          )}

          {successMessage ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
              <CheckCircle sx={{ color: 'green', fontSize: 60, mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'green' }}>
                {successMessage}
              </Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                label="Full Name"
                variant="filled"
                name="name"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderRadius: '8px',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                    '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.1)' },
                  },
                }}
                InputLabelProps={{
                  sx: { color: 'rgba(255,255,255,0.7)', fontWeight: 500 },
                }}
                sx={{ '& input': { color: '#fff', py: 2, px: 2.5 } }}
              />

              <TextField
                label="Email Address"
                variant="filled"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderRadius: '8px',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                    '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.1)' },
                  },
                }}
                InputLabelProps={{
                  sx: { color: 'rgba(255,255,255,0.7)', fontWeight: 500 },
                }}
                sx={{ '& input': { color: '#fff', py: 2, px: 2.5 } }}
              />

              <FormControl component="fieldset" required>
                <FormLabel
                  component="legend"
                  sx={{ color: 'rgba(255,215,0,0.9)', mb: 2, fontWeight: 600, fontSize: '1rem' }}
                >
                  Where did you hear about us?
                </FormLabel>
                <RadioGroup name="referral" value={formData.referral} onChange={handleChange}>
                  <Grid container spacing={2}>
                    {referralOptions.map((option) => (
                      <Grid item xs={6} sm={4} key={option.value}>
                        <FormControlLabel
                          value={option.value}
                          control={<Radio sx={{ display: 'none' }} />}
                          label={
                            <Box
                              sx={{
                                width: '150px',
                                p: 2,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '12px',
                                bgcolor:
                                  formData.referral === option.value
                                    ? 'rgba(255,215,0,0.1)'
                                    : 'rgba(255,255,255,0.05)',
                                border:
                                  formData.referral === option.value
                                    ? '2px solid #FFD700'
                                    : '2px solid transparent',
                                transition: 'all 0.2s ease',
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'rgba(255,215,0,0.08)',
                                  transform: 'translateY(-2px)',
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    formData.referral === option.value
                                      ? '#FFD700'
                                      : 'rgba(255,255,255,0.8)',
                                  fontWeight: 500,
                                  textAlign: 'center',
                                }}
                              >
                                {option.label}
                              </Typography>
                            </Box>
                          }
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </RadioGroup>
              </FormControl>

              <TextField
                label="Referral Code (optional)"
                variant="filled"
                name="referralCode"
                value={formData.referralCode}
                onChange={handleChange}
                fullWidth
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderRadius: '8px',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                    '&.Mui-focused': { bgcolor: 'rgba(255,255,255,0.1)' },
                  },
                }}
                InputLabelProps={{
                  sx: { color: 'rgba(255,255,255,0.7)', fontWeight: 500 },
                }}
                sx={{ '& input': { color: '#fff', py: 2, px: 2.5 } }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{
                  py: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(45deg, #FFD700 0%, #FFC400 100%)',
                  color: '#000',
                  fontWeight: 800,
                  fontSize: '1.1rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 20px rgba(255, 215, 0, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #FFC400 0%, #FFD700 100%)',
                    boxShadow: '0 6px 24px rgba(255, 215, 0, 0.4)',
                  },
                }}
              >
                Get Early Access
              </Button>
            </Box>
          )}

          <Typography
            variant="caption"
            sx={{
              mt: 3,
              display: 'block',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.8rem',
              lineHeight: 1.4,
            }}
          >
            By continuing, you agree to our Terms of Service and Privacy Policy.
            <br />
            We'll never share your information with third parties.
          </Typography>
        </Box>
      </Container>
    </>
  );
}

export default SignUp;
